// @flow
import React from 'react';
import styled from 'styled-components';
import { colors, fontWeight } from '../constants';
import TristateCheckboxWithLabel from './tristate_checkbox_with_label';

type OptionData = {
  label: string,
  value: string | number,
  note?: string,
  disabled?: boolean,
};

type OptionGroupData = {
  label: string,
  options: Array<OptionData>,
};

type Props = {
  mode: 'checkbox' | 'radio',
  radioOnText: 'Show' | 'Hide',
  radioOffText: 'Show' | 'Hide',
  error?: string | any,
  height?: string,
  toggleSelectedId: (any, boolean) => void,
  options: Array<OptionGroupData>,
  selected: Array<string>,
};

export default class StackedTable extends React.Component<Props> {
  static defaultProps = {
    mode: 'checkbox',
    radioOnText: 'Show',
    radioOffText: 'Hide',
  };

  render() {
    return (
      <List error={this.props.error} height={this.props.height}>
        {this.props.options.map((optionGroupData) =>
          this.renderGroup(optionGroupData)
        )}
      </List>
    );
  }

  renderGroup(optionGroup: OptionGroupData) {
    const { mode } = this.props;

    return (
      <div
        key={optionGroup.label}
        data-provides="stacked-table-group"
        data-provides-for={optionGroup.label}
      >
        <OptionGroup>
          {optionGroup.label}
          {mode === 'radio' && this.radioLabels()}
        </OptionGroup>

        {optionGroup.options.map((option) => this.renderOption(option))}
      </div>
    );
  }

  radioLabels() {
    const { radioOnText, radioOffText } = this.props;

    return (
      <RadioLabels>
        <span>{radioOnText}</span>
        <span>{radioOffText}</span>
      </RadioLabels>
    );
  }

  renderOption(option: OptionData) {
    const { toggleSelectedId, selected, mode } = this.props;
    const optionSelected = selected.indexOf(option.value) >= 0;

    return (
      <Option
        key={option.value}
        data-provides="stacked-table-option"
        data-provides-for={option.value}
        highlight={optionSelected && mode === 'checkbox'}
      >
        {mode === 'radio' && (
          <>
            {option.label}
            {option.note && <Note>{option.note}</Note>}
          </>
        )}

        {mode === 'checkbox' && (
          <TristateCheckboxWithLabel
            checkedState={optionSelected ? 'checked' : 'empty'}
            type="checkbox"
            name={option.value.toString()}
            onChange={() => toggleSelectedId(option.value, !optionSelected)}
            label={
              <>
                {option.label}
                {option.note && <Note>{option.note}</Note>}
              </>
            }
            disabled={option.disabled}
          />
        )}

        {mode === 'radio' && (
          <RadioButtons>
            <input
              type="radio"
              checked={optionSelected}
              onChange={(_event) => toggleSelectedId(option.value, true)}
              disabled={option.disabled}
            />
            <input
              type="radio"
              checked={!optionSelected}
              onChange={(_event) => toggleSelectedId(option.value, false)}
              disabled={option.disabled}
            />
          </RadioButtons>
        )}
      </Option>
    );
  }
}

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const List = styled.div`
  margin-top: 10px;
  border: 1px solid
    ${(props) => (props.error ? colors.darkRed : colors.lightGrey)};
  border-bottom: none;
  border-radius: 2px;
  height: ${(props) => props.height || 'auto'};
  overflow-y: auto;
`;

const OptionGroup = styled.div`
  background-color: ${colors.lightestGrey};
  border-bottom: solid 1px ${colors.lightGrey};
  padding: 5px 15px;
  ${fontWeight('normal')};
  font-size: 12px;
  color: ${colors.darkGrey};
  position: sticky;
  top: 0;
`;
OptionGroup.displayName = 'OptionGroup';

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const Option = styled.label`
  ${fontWeight('normal')};
  border-bottom: solid 1px ${colors.lightGrey};
  padding: 10px 15px;
  min-height: 46px;
  display: block;
  width: 100%;
  box-sizing: border-box;
  background-color: ${({ highlight }) =>
    highlight ? colors.babyBlue : 'inherit'};
`;
Option.displayName = 'Option';

const Note = styled.span`
  font-size: 12px;
  ${fontWeight('normal')};
  color: ${colors.darkGrey};
  margin-left: 5px;
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const RadioLabels = styled.div`
  float: right;

  span:first-child {
    margin-right: 35px;
  }

  span:last-child {
    margin-right: 25px;
  }
`;

const RadioButtons = styled.div`
  float: right;

  input:first-child {
    margin-right: 50px;
  }

  input:last-child {
    margin-right: 30px;
  }
`;
